// M040 C2: hardcoded colors → tokens, text-* → text-tok-*
// data-fit LLC · DecisionFlow · EXECUTE Stage
// StatementBatchManager.jsx · DDL: R5 · API: v1.11 · Session 92
// Auth: useAuth() direct — ARCH-2 compliant
// Key constraints: actor separation advisory, inline confirmations, fiscal_period_id integer
// PATCH-S92-05: loadBatch(id) added — GET /api/v1/statement-batches/{id} on manual ID entry

import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useAuth } from '../../auth/AuthContext.jsx'
import { SCREEN_ORDER } from './executeFlowConstants'

const BATCH_STATUSES = ['draft', 'pending_approval', 'released']

function getErrorMessage(payload) {
  const error = payload?.error
  if (!error) return 'Unexpected statement batch error.'

  if (error.code === 'STATEMENT_NOT_FOUND') {
    return 'Statement or statement batch was not found.'
  }

  if (error.code === 'STATEMENT_BATCH_NOT_RELEASED') {
    return 'The statement batch has not been released.'
  }

  if (error.code === 'FISCAL_PERIOD_MISMATCH') {
    return 'The selected statement batch does not match the fiscal period.'
  }

  if (error.code === 'SELF_APPROVAL_BLOCKED') {
    return 'Statement batches cannot be released by the same user who submitted them. Ask a second comp admin to release this batch.'
  }

  if (error.code === 'INVALID_STATUS_TRANSITION') {
    return error.recovery_action || 'Invalid statement batch transition.'
  }

  if (error.code === 'AUTH_INSUFFICIENT_ROLE') {
    return 'You do not have the comp_admin role required for statement batches.'
  }

  return error.recovery_action || error.message || 'Unable to manage statement batch.'
}

export default function StatementBatchManager({ fiscalPeriodId, fiscalPeriodLabel, runId }) {
  const { token, user } = useAuth()

  const [batch, setBatch] = useState(null)
  const [statementBatchId, setStatementBatchId] = useState('')
  const [confirmation, setConfirmation] = useState(null)
  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [successMessage, setSuccessMessage] = useState('')

  const periodId = useMemo(() => {
    const parsed = Number(fiscalPeriodId)
    return Number.isInteger(parsed) ? parsed : null
  }, [fiscalPeriodId])

  const currentUserId = user?.id || user?.user_id || user?.email || null

  // OI-S214-02: auto-load existing batch for this fiscal period on mount.
  // Guard ref prevents double-fire on re-render.
  // Strategy: fetch list filtered by fiscal_period_id, then prefer batch matching
  // runId (if available), else take most recent (data[0], ordered DESC by created_at).
  const autoLoadAttempted = useRef(false)

  useEffect(() => {
    if (autoLoadAttempted.current) return
    if (!periodId) return
    autoLoadAttempted.current = true

    async function autoLoad() {
      setLoading(true)
      try {
        const response = await fetch(
          `/api/v1/statement-batches?fiscal_period_id=${periodId}`,
          { credentials: 'include' }
        )
        if (!response.ok) return // silent — user can load/create manually
        const payload = await response.json().catch(() => ({}))
        const batches = payload?.data || []
        if (!batches.length) return // no batch yet — user creates manually

        // Prefer batch tied to the current runId; fall back to most recent
        const matched = runId
          ? batches.find((b) => b.calculation_run_id === runId) || batches[0]
          : batches[0]

        setBatch(matched)
        setStatementBatchId(matched.statement_batch_id || '')
      } catch {
        // silent — auto-load failure does not block manual flow
      } finally {
        setLoading(false)
      }
    }

    autoLoad()
  }, [periodId, runId])
  const loadBatch = async (id) => {
    const targetId = id || statementBatchId.trim()
    if (!targetId) {
      setErrorMessage('Enter a statement batch ID to load.')
      return
    }

    setLoading(true)
    setErrorMessage('')
    setSuccessMessage('')

    try {
      const response = await fetch(`/api/v1/statement-batches/${targetId}`, {
        credentials: 'include',
      })

      const payload = await response.json().catch(() => ({}))

      if (!response.ok) {
        throw new Error(getErrorMessage(payload))
      }

      const nextBatch = payload?.data || payload
      setBatch(nextBatch)
      setStatementBatchId(nextBatch?.statement_batch_id || targetId)
      setSuccessMessage('Statement batch loaded.')
    } catch (error) {
      setErrorMessage(error.message || 'Unable to load statement batch.')
    } finally {
      setLoading(false)
    }
  }

  const createBatch = async () => {
    setErrorMessage('')
    setSuccessMessage('')

    if (!periodId) {
      setErrorMessage('A valid integer fiscal_period_id is required.')
      return
    }

    if (!runId) {
      setErrorMessage('A calculation_run_id is required to create a statement batch. Complete a calculation run first.')
      return
    }

    setLoading(true)

    try {
      const response = await fetch('/api/v1/statement-batches', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fiscal_period_id: Number(periodId),
          calculation_run_id: runId,
        }),
      })

      const payload = await response.json().catch(() => ({}))

      if (!response.ok) {
        throw new Error(getErrorMessage(payload))
      }

      const nextBatch = payload?.data || payload
      setBatch(nextBatch)
      setStatementBatchId(nextBatch?.statement_batch_id || '')
      setSuccessMessage('Statement batch created in draft status.')
    } catch (error) {
      setErrorMessage(error.message || 'Unable to create statement batch.')
    } finally {
      setLoading(false)
    }
  }

  const submitBatch = async () => {
    const id = batch?.statement_batch_id || statementBatchId.trim()
    if (!id) {
      setErrorMessage('statement_batch_id is required.')
      return
    }

    setLoading(true)
    setErrorMessage('')
    setSuccessMessage('')

    try {
      // POST /api/v1/statement-batches/{id}/submit — no request body (v1.24 §15.2)
      const response = await fetch(`/api/v1/statement-batches/${id}/submit`, {
        method: 'POST',
        credentials: 'include',
      })

      const payload = await response.json().catch(() => ({}))

      if (!response.ok) {
        throw new Error(getErrorMessage(payload))
      }

      const nextBatch = payload?.data || {
        ...batch,
        statement_batch_id: id,
        batch_status: 'pending_approval',
      }

      setBatch(nextBatch)
      setSuccessMessage('Statement batch submitted for approval.')
      setConfirmation(null)
    } catch (error) {
      setErrorMessage(error.message || 'Unable to submit statement batch.')
    } finally {
      setLoading(false)
    }
  }

  const releaseBatch = async () => {
    const id = batch?.statement_batch_id || statementBatchId.trim()
    if (!id) {
      setErrorMessage('statement_batch_id is required.')
      return
    }

    setLoading(true)
    setErrorMessage('')
    setSuccessMessage('')

    try {
      // POST /api/v1/statement-batches/{id}/release — no request body (v1.24 §15.3)
      const response = await fetch(`/api/v1/statement-batches/${id}/release`, {
        method: 'POST',
        credentials: 'include',
      })

      const payload = await response.json().catch(() => ({}))

      if (!response.ok) {
        throw new Error(getErrorMessage(payload))
      }

      const nextBatch = payload?.data || {
        ...batch,
        statement_batch_id: id,
        batch_status: 'released',
      }

      setBatch(nextBatch)
      setSuccessMessage('Statement batch released.')
      setConfirmation(null)
    } catch (error) {
      setErrorMessage(error.message || 'Unable to release statement batch.')
    } finally {
      setLoading(false)
    }
  }

  const status = BATCH_STATUSES.includes(batch?.batch_status)
    ? batch.batch_status
    : 'draft'

  const submittedBy = batch?.submitted_by || batch?.submitted_by_user_id
  const releaseActorWarning =
    currentUserId && submittedBy && String(currentUserId) === String(submittedBy)

  const requestSubmit = () => {
    setConfirmation({
      type: 'submit',
      title: 'Submit statement batch?',
      message: 'This moves the batch from draft to pending_approval.',
      confirmLabel: 'Submit batch',
      onConfirm: submitBatch,
    })
  }

  const requestRelease = () => {
    setConfirmation({
      type: 'release',
      title: 'Release statement batch?',
      message: releaseActorWarning
        ? 'Four-eyes required: you submitted this batch and cannot release it. The server will block this action. Ask a second comp admin to release.'
        : 'This releases the statement batch to reps. A different user from the submitter is required.',
      confirmLabel: 'Release batch',
      onConfirm: releaseBatch,
    })
  }

  const goNext = () => {
    window.dispatchEvent(
      new CustomEvent('decisionflow:navigate', {
        detail: { screen: SCREEN_ORDER[5] },
      }),
    )
  }

  return (
    <section className="space-y-6">
      <header>
        <p className="text-tok-xs font-semibold uppercase tracking-widest text-[rgb(var(--color-primary))] mb-1">EXEC-05</p>
        <h2 className="text-tok-2xl font-bold text-[rgb(var(--color-text))]">Statement Batch Manager</h2>
        <p className="text-tok-sm text-[rgb(var(--color-text-muted))] mt-1">Manage statement batch lifecycle from draft to release.</p>
      </header>

      {errorMessage && (
        <div className="rounded-md bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 text-[rgb(var(--color-danger))] px-4 py-3 text-tok-sm">{errorMessage}</div>
      )}
      {successMessage && (
        <div className="rounded-md bg-[rgb(var(--color-success))]/10 border border-[rgb(var(--color-success))]/30 text-[rgb(var(--color-success))] px-4 py-3 text-tok-sm">{successMessage}</div>
      )}

      <div className="bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] rounded-lg p-6 space-y-4">
        <label className="block text-tok-sm font-medium text-[rgb(var(--color-text))]">
          Fiscal Period
          <input value={fiscalPeriodLabel || periodId || ''} readOnly className="mt-1 block w-full rounded border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text-muted))]" />
        </label>

        <label className="block text-tok-sm font-medium text-[rgb(var(--color-text))]">
          Statement Batch ID
          <input
            value={statementBatchId}
            onChange={(event) => setStatementBatchId(event.target.value)}
            placeholder="Enter existing batch ID or create new"
            className="mt-1 block w-full rounded border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-3 py-2 text-tok-sm focus:outline-none"
          />
        </label>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => loadBatch()}
            disabled={loading || !statementBatchId.trim()}
            className="px-4 py-2 rounded border border-[rgb(var(--color-border))] text-[rgb(var(--color-text))] text-tok-sm font-medium hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50"
          >
            {loading ? 'Loading…' : 'Load existing batch'}
          </button>
          <button type="button" onClick={createBatch} disabled={loading} className="px-4 py-2 rounded bg-[rgb(var(--color-primary))] text-white text-tok-sm font-medium hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50">
            {loading ? 'Working…' : 'Create draft batch'}
          </button>
        </div>
      </div>

      <article className="bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] rounded-lg p-6">
        <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))] mb-4">Current Batch</h3>
        {batch ? (
          <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-tok-sm mb-4">
            <dt className="font-medium text-[rgb(var(--color-text-muted))]">Batch ID</dt>
            <dd className="text-[rgb(var(--color-text))] font-mono text-tok-xs">{batch.statement_batch_id}</dd>
            <dt className="font-medium text-[rgb(var(--color-text-muted))]">Status</dt>
            <dd className="text-[rgb(var(--color-text))]">{status}</dd>
            <dt className="font-medium text-[rgb(var(--color-text-muted))]">Submitted By</dt>
            <dd className="text-[rgb(var(--color-text))]">{submittedBy || '—'}</dd>
            <dt className="font-medium text-[rgb(var(--color-text-muted))]">Released By</dt>
            <dd className="text-[rgb(var(--color-text))]">{batch.released_by || batch.released_by_user_id || '—'}</dd>
          </dl>
        ) : (
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))] italic mb-4">No statement batch loaded yet.</p>
        )}

        {/* OI-ACTOR-SEPARATION-GLOBAL: inline warning when submitter tries to release */}
        {releaseActorWarning && status === 'pending_approval' && (
          <p className="flex items-center gap-2 rounded border border-[rgb(var(--color-warning))]/40 bg-[rgb(var(--color-warning))]/10 px-3 py-2 text-tok-xs text-[rgb(var(--color-warning))]">
            <span>⚠</span>
            You submitted this — a second comp admin must approve it.
          </p>
        )}
        <div className="flex gap-2">
          <button
            type="button"
            disabled={loading || status !== 'draft'}
            onClick={requestSubmit}
            className="px-4 py-2 rounded bg-[rgb(var(--color-primary))] text-white text-tok-sm font-medium hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50"
          >
            Submit
          </button>
          <button
            type="button"
            disabled={loading || status !== 'pending_approval' || releaseActorWarning}
            onClick={requestRelease}
            className="px-4 py-2 rounded bg-[rgb(var(--color-primary))] text-white text-tok-sm font-medium hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50"
          >
            Release
          </button>
          <button
            type="button"
            disabled={status !== 'released'}
            onClick={goNext}
            className="px-4 py-2 rounded bg-[rgb(var(--color-success))] text-white text-tok-sm font-medium hover:bg-[rgb(var(--color-success))]/80 disabled:opacity-50"
          >
            Continue to payroll export
          </button>
        </div>
      </article>

      {confirmation && (
        <div className="fixed inset-0 bg-[rgb(var(--color-text))]/40 flex items-center justify-center z-50" role="dialog" aria-modal="true">
          <div className="bg-[rgb(var(--color-surface))] rounded-lg shadow-xl p-6 max-w-md w-full space-y-4">
            <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">{confirmation.title}</h3>
            <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">{confirmation.message}</p>
            {confirmation.type === 'release' && releaseActorWarning && (
              <p className="text-tok-xs text-[rgb(var(--color-warning))] bg-[rgb(var(--color-warning))]/10 border border-[rgb(var(--color-warning))]/30 rounded px-3 py-2">
                Advisory only: database/server actor separation remains
                authoritative.
              </p>
            )}
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => setConfirmation(null)} className="px-4 py-2 rounded border border-[rgb(var(--color-border))] text-[rgb(var(--color-text))] text-tok-sm font-medium hover:bg-[rgb(var(--color-surface-muted))]">
                Cancel
              </button>
              <button
                type="button"
                disabled={loading}
                onClick={confirmation.onConfirm}
                className="px-4 py-2 rounded bg-[rgb(var(--color-primary))] text-white text-tok-sm font-medium hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50"
              >
                {confirmation.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}
